import type {
  ContentProvider,
  ContentSelectionContext,
  LearningContentItem,
} from '../../sdk/content';
import type { SemanticInputReader } from '../../sdk/input';
import type {
  MissionDefinition,
  MissionStageDefinition,
  SessionAnalyticsWriter,
} from '../../sdk/mission';
import type { NarrationService } from '../../sdk/narration';
import type { AdaptationPolicy, AdaptationState } from '../../sdk/adaptation';
import type { GameProgressionProvider } from '../../sdk/progression';
import type { ScoreController, ScoreState, ScoringStrategy } from '../../sdk/scoring';
import type {
  TeachingMomentController,
  TeachingMomentDefinition,
  TeachingProvider,
} from '../../sdk/teaching';
import type { GameId, PlayerId, SkillBand } from '../../shared/types/ids';
import type { ThriveInputEvent } from '../../shared/types/input-events';
import type { MissionProgressSnapshot } from '../../shared/types/mission-progress';
import type { PlayerMode, PlayerRuntimeState } from '../../shared/types/players';
import type {
  CompletedGameSession,
  GameSessionContext,
  SessionRecord,
} from '../../shared/types/session';
import type { SkillEstimate, SkillEvidenceResult } from '../../shared/types/skills';
import { APPLIED_SESSION_HISTORY_LIMIT } from '../../shared/types/progress';
import type { GameplayTunables } from '../../shared/types/tunables';
import type {
  LaunchIdentityRepository,
  MissionProgressRepository,
  ProgressRepository,
  SessionRepository,
  SkillRepository,
} from '../../storage/interfaces/repositories';
import { createSkillEvidence } from '../skills/evidence';
import { learningSubjectIdOf } from '../skills/learning-subject';
import { inRosterOrder } from './roster-order';
import type { ParentOverrideCommand } from '../runtime/app-events';
import { createSessionAnalyticsWriter } from './analytics-writer';
import {
  nextStepAfterMiss,
  nextStepAfterTimeout,
  scoringEventTypeFor,
  type LadderContext,
  type LadderStep,
} from './attempt-ladder';
import type { MissionRuntimeObserver, RoundMoveOnReason, RoundPrompt } from './mission-events';
import {
  createResponseMatcher,
  isOnsetInputEvent,
  isSupportedResponseMode,
  type MatcherVerdict,
} from './response-matchers';
import { createTimerGroup, type RoundTimerId, type TimerGroup } from './round-timers';
import { createScoreController } from './score-controller';
import { createSystemScheduler, type Scheduler } from './scheduler';
import { assembleCompletedSession } from './session-assembly';
import { createMissionTeachingController } from './teaching-controller';

/**
 * The mission runtime.
 *
 * It walks `MissionDefinition.stages`, runs each scored round through one lifecycle — present,
 * await a response, resolve, feed back, next — and owns everything the boundary in D-034 PS-3
 * assigns to the platform: response resolution against `ExpectedResponse`, the attempt ladder,
 * the 8/15/25 s timer ladder, scoring, teaching, evidence and session assembly. A scene subscribes
 * to `MissionRuntimeEvent` and draws. It is never asked whether an answer was right, which is what
 * makes the keyboard path and the (Phase 3) camera path one path.
 *
 * The product rules that live here, and cannot live anywhere else:
 *
 *   - a wrong answer costs nothing. There is no code path from a miss to the score.
 *   - a round timer expiring escalates the *help* offered. It does not consume an attempt and it
 *     records no `incorrect` evidence (MVP notes §12, D-021).
 *   - the ladder always terminates: retry → teaching → simplified → move on (§13).
 *   - nothing recorded is a ranking, a speed or a per-child score (architecture §12).
 */

export type MissionRuntimeStatus = 'idle' | 'running' | 'paused' | 'completed' | 'disposed';

export interface MissionRuntimeRepositories {
  sessions: SessionRepository;
  skills: SkillRepository;
  missionProgress: MissionProgressRepository;
  /**
   * Who launched the sitting (D-054(2), Slice R).
   *
   * Optional on the same principle as `progress` below: a harness that has no identity store is
   * not forced to invent one, and its absence means the record is simply not cleared here — never
   * that half the clear runs. In the app it is always present, because the two records describe
   * one sitting and are cleared together.
   */
  launchIdentity?: LaunchIdentityRepository;
  /**
   * Where a finished session's progression lands (R1-11).
   *
   * Optional so a caller that genuinely has no progress store — a test harness, a future preview
   * mode — is not forced to invent one; a missing store means the transaction is skipped, never
   * that it silently half-runs.
   */
  progress?: ProgressRepository;
}

export interface MissionRuntimeOptions {
  mission: MissionDefinition;
  gameId: GameId;
  content: ContentProvider;
  scoring: ScoringStrategy;
  teaching: TeachingProvider;
  /**
   * The game's own progression store-keeper (module spec §18).
   *
   * Optional, and when it is absent nothing is persisted beyond the session record — but with it
   * the platform runs the once-per-completed-session transaction that D-039(5) assumed somebody
   * was running: load or create progress, let the module fold the finished session in, save.
   */
  progression?: GameProgressionProvider;
  /**
   * The game's own adaptation policy (module spec §13, SKL-04).
   *
   * Optional; when absent every position is served at the configured band, exactly as before.
   */
  adaptation?: AdaptationPolicy;
  session: GameSessionContext;
  players: PlayerRuntimeState[];
  playerMode: PlayerMode;
  tunables: GameplayTunables;
  input: SemanticInputReader;
  narration: NarrationService;
  repositories: MissionRuntimeRepositories;
  onEvent: MissionRuntimeObserver;
  /**
   * Whether this sitting plays the curated first-playthrough sequence. Defaults to `true`.
   *
   * A grown-up's "play again" turns it off, and this is the only route that choice has to a
   * content provider — the runtime builds every `ContentSelectionContext` the mission uses
   * (GLP-02, D-037(8)).
   */
  useCuratedSequence?: boolean;
  scheduler?: Scheduler;
  /** Wall-clock source for ISO timestamps; injected in tests. */
  clock?: () => Date;
}

/**
 * The platform-owned controllers a `GameSceneContext` is assembled from (module spec §9).
 *
 * Exposed rather than rebuilt by the shell: a scene handed a second score controller would be
 * reading a different total from the one the mission is keeping, and a second teaching
 * controller would carry its own copy of the one-moment-per-player cap. They are the SDK's
 * narrow interfaces, so a scene still cannot reach the state behind them.
 */
export interface MissionSceneServices {
  scoring: ScoreController;
  teaching: TeachingMomentController;
  analytics: SessionAnalyticsWriter;
}

export interface MissionRuntime {
  /** Runs the mission to completion. Pass a snapshot to resume an interrupted one (PRO-03). */
  start(resume?: MissionProgressSnapshot | null): Promise<void>;
  /** What a scene is allowed to touch of the runtime (module spec §9). */
  readonly sceneServices: MissionSceneServices;
  pause(): void;
  resume(): void;
  /** "Say that again" — the R key and the shell's repeat control (GDS §31). */
  repeatInstruction(): void;
  applyParentOverride(command: ParentOverrideCommand): void;
  dispose(): void;
  readonly status: MissionRuntimeStatus;
}

const BAND_ORDER: readonly SkillBand[] = ['explorer', 'hero', 'master'];

function lowerBand(band: SkillBand): SkillBand | null {
  const index = BAND_ORDER.indexOf(band);
  return index > 0 ? (BAND_ORDER[index - 1] ?? null) : null;
}

/** The safer band for a shared challenge: the lowest any player is on (MVP notes §7). */
function teamBandOf(players: readonly PlayerRuntimeState[]): SkillBand {
  let lowest = BAND_ORDER.length - 1;
  for (const player of players) {
    const index = BAND_ORDER.indexOf(player.band);
    if (index >= 0 && index < lowest) lowest = index;
  }
  return BAND_ORDER[lowest] ?? 'explorer';
}

function isScoredStage(stage: MissionStageDefinition): boolean {
  return stage.contentSelector !== undefined || stage.completionRule.type === 'ROUND_COUNT';
}

function promptOf(item: LearningContentItem): RoundPrompt {
  const prompt: RoundPrompt = {
    text: item.prompt.text,
    spokenInstruction: item.prompt.spokenInstruction,
    inputMethod: item.prompt.inputMethod,
  };
  if (item.prompt.choices !== undefined) prompt.choices = item.prompt.choices;
  if (item.prompt.visualAssetId !== undefined) prompt.visualAssetId = item.prompt.visualAssetId;
  return prompt;
}

/**
 * How a round's listening ends.
 *
 * A wrong answer is deliberately not on this list. In cooperative play a miss from one child
 * never closes the round (D-045 PS2-1) — it is handled while the listening stays open, and only
 * a rung the ladder cannot take *inside* the round (teaching, the simplified variant, moving on)
 * comes back here as `LADDER_STEP`. Single player is the one-player case of the same rule: the
 * first miss is a retry handled in place, exactly as re-entering `awaitResponse` used to do it.
 */
type RoundResponse =
  | {
      kind: 'CORRECT';
      playerId: PlayerId;
      /**
       * Who else matched inside the participation grace window (COOP-03). Resolved when the
       * window closes; the child's success does not wait for it.
       */
      graceParticipants: Promise<readonly PlayerId[]>;
    }
  /** The team's misses reached a rung that has to be taken outside the round's listening. */
  | { kind: 'LADDER_STEP'; step: LadderStep }
  /** The 25 s step was reached with nothing answered. Never a wrong answer. */
  | { kind: 'TIMEOUT' }
  | { kind: 'OVERRIDE'; command: ParentOverrideCommand }
  /** The stage went away underneath us. */
  | { kind: 'ABORTED' };

/** What the round loop tells the resolver to do about a miss it has just written down. */
type MissDisposition =
  /** The listening stays open — the retry is already on screen. */
  | { disposition: 'CONTINUE' }
  /** The round has to leave its listening to take this rung. */
  | { disposition: 'STOP'; step: LadderStep };

/**
 * How the round loop is told about a miss.
 *
 * Deliberately in two halves. The outer call writes the miss down **synchronously**: two children
 * can miss in the same tick, and a miss that lands before the other child's winning answer has to
 * be on the record — and to have spent its rung of the shared ladder — before that answer reads
 * the shard tier. Deferring it by even one microtask lost the miss entirely. The returned thunk is
 * the rest: make it durable, then either put the retry back up or hand the rung back.
 */
type MissHandler = (playerId: PlayerId) => (stillOpen: () => boolean) => Promise<MissDisposition>;

export function createMissionRuntime(options: MissionRuntimeOptions): MissionRuntime {
  const scheduler = options.scheduler ?? createSystemScheduler();
  /**
   * Every multi-player id array this runtime emits, in lane order rather than arrival order
   * (R1-8, COOP-07). One helper, applied at every site that turns a set of responders into a
   * list, so "who took part" can never become "who went first".
   */
  const rosterOrder = (playerIds: Iterable<PlayerId>): PlayerId[] =>
    inRosterOrder(options.players, playerIds);
  const clock = options.clock ?? (() => new Date());
  const timers: TimerGroup = createTimerGroup(scheduler);

  const analytics = createSessionAnalyticsWriter({
    gameId: options.gameId,
    missionId: options.mission.id,
    playerIds: options.players.map((player) => player.playerId),
    now: clock,
  });

  const score = createScoreController({
    strategy: options.scoring,
    context: {
      players: options.players,
      playerMode: options.playerMode,
      mission: options.mission,
      tunables: options.tunables,
    },
    onChange: (visible) => {
      options.onEvent({ type: 'SCORE_CHANGED', score: visible });
    },
  });

  const teaching = createMissionTeachingController({
    provider: options.teaching,
    narration: options.narration,
    scheduler,
    onEvent: options.onEvent,
  });

  let status: MissionRuntimeStatus = 'idle';
  /**
   * Read through helpers rather than compared inline: `status` is reassigned from several
   * closures, and a direct comparison narrows to whatever the compiler last saw assigned.
   */
  const isDisposed = (): boolean => status === 'disposed';
  const isRunning = (): boolean => status === 'running';
  let roundIndex = 0;
  let startedAtMs = scheduler.now();
  const completedContentIds: string[] = [];
  /** Set while a round is waiting, so pause, override and dispose can reach it. */
  let abortRound: ((response: RoundResponse) => void) | null = null;
  /**
   * Throws away the answers that were still in flight when the mission stopped listening (S1-4).
   *
   * D-059 PS3-2 asks for exactly this — "in-flight candidates are discarded" — and until now only
   * their *timers* were suspended. A 650 ms portal dwell interrupted at 600 ms therefore finished
   * its last 50 ms after the mission came back and committed a portal the child never completed.
   * The interruption is very often an input failure (a camera that lost the child), and an input
   * failure that writes down a wrong answer is the one thing the whole confidence design exists to
   * prevent (CAM-05, MIN §13).
   *
   * Set beside `abortRound` and for the same reason: only the round that is currently listening
   * knows what it has armed.
   */
  let discardArmedAnswers: (() => void) | null = null;
  let endSessionRequested = false;
  let currentSpokenInstruction: string | null = null;
  /**
   * An override pressed when no round is waiting for an answer — during a teaching moment, or
   * between rounds. It used to be dropped on the floor, because `abortRound` is the only route a
   * command had and it exists only inside `awaitResponse` (R1-5).
   */
  let pendingOverride: ParentOverrideCommand | null = null;
  /**
   * Which sequence this sitting is playing. Mutable because a resumed snapshot's provenance wins
   * over a launch config a refresh recomputed from defaults (R1-8).
   */
  let useCuratedSequence = options.useCuratedSequence ?? true;
  /** Where an interrupted round was, consumed by the first round this sitting runs (R1-3). */
  let resumeInto: {
    attemptNumber: number;
    onSimplified: boolean;
    sawIncorrectAnswer: boolean;
    observedPlayerIds: readonly PlayerId[];
    contentId?: string;
  } | null = null;
  /**
   * Stored estimates for this sitting's players, loaded once (R1-13), **kept per player**.
   *
   * The adaptation policy reads them at each content position; re-reading storage fourteen times
   * for a table that cannot change mid-mission would be a cost with no behaviour behind it.
   *
   * The grouping is the load-bearing part (SKL-05 ruling, Slice E). Flattening the two arrays
   * threw away whose history was whose, and "has the team met this concept?" is not the question
   * the documents ask: a skill Hero 1 knows and Hero 2 has never seen is new *to a child in the
   * room*, and the team should meet it at the gentler band (MVP notes §7, GDS §23). The policy
   * still receives the flattened list — it reasons about the team's history, not about which
   * child owns which row, and per-child estimates on a game-facing seam would be the per-child
   * performance structure the non-competitive invariant keeps out of game code.
   */
  let estimatesPromise: Promise<readonly (readonly SkillEstimate[])[]> | null = null;
  const storedEstimatesByPlayer = async (): Promise<readonly (readonly SkillEstimate[])[]> => {
    estimatesPromise ??= Promise.all(
      options.players.map((player) => {
        // Keyed by the child, never by the seat, and skipped entirely for a guest (R1-3).
        const subjectId = learningSubjectIdOf(player);
        return subjectId === null
          ? Promise.resolve<readonly SkillEstimate[]>([])
          : options.repositories.skills.getSkillEstimates(subjectId);
      }),
    ).catch(() => []);
    return estimatesPromise;
  };
  /** The policy's own working state for this sitting. Never shown to a child. */
  let adaptationState: AdaptationState | null = null;

  /**
   * This sitting's lane → durable-subject table (R1-3). Built once: the roster cannot change
   * mid-mission (D-037(1)), and a per-row profile lookup would be a storage read per skill.
   */
  const subjectByPlayerId = new Map<PlayerId, PlayerId | null>(
    options.players.map((player) => [player.playerId, learningSubjectIdOf(player)]),
  );
  /** `null` for a guest, and for any id that is not on this sitting's roster. */
  const subjectFor = (playerId: PlayerId): PlayerId | null =>
    subjectByPlayerId.get(playerId) ?? null;

  const selectionContext = (): ContentSelectionContext => ({
    players: options.players,
    playerMode: options.playerMode,
    teamBand: teamBandOf(options.players),
    useCuratedSequence,
    alreadyUsedContentIds: [...completedContentIds],
  });

  const sessionRecord = (): SessionRecord => ({
    sessionId: options.session.sessionId,
    gameId: options.session.gameId,
    missionId: options.session.missionId,
    playerMode: options.session.playerMode,
    inputMode: options.session.inputMode,
    players: options.players,
    startedAt: options.session.startedAt,
  });

  const speak = (text: string): void => {
    // Fire and forget: narration must never be the thing a round is waiting for. A speech engine
    // that stalls cannot be allowed to stall a child's turn.
    void options.narration.speak(text, { interrupt: true }).catch(() => {
      // A narration failure is silent by design; the on-screen prompt still carries the round.
    });
  };

  /**
   * Everything about the round in flight that a resume has to put back (R1-3, R2-3).
   *
   * An options object rather than five positional arguments because the set grew: what a round
   * has *already observed* is as much part of "where were we" as which attempt it is on, and a
   * boolean in the fourth position of a call is not something a reader can check.
   */
  interface RoundCheckpoint {
    stageId: string;
    attemptNumber: number;
    onSimplified: boolean;
    contentId?: string;
    sawIncorrectAnswer: boolean;
    observedPlayerIds: readonly PlayerId[];
  }

  /**
   * Every write to the one snapshot slot, in the order the mission decided to make them (R1-1).
   *
   * There is a single slot and more than one thing that writes to it: a miss checkpoint, a round
   * closing, a teaching cap being spent, the clear at mission end. Two of those can be in flight
   * at once — a miss checkpoint starts writing, the other child's correct answer wins the round
   * while it is still going — and the repository is under no obligation to finish them in the
   * order it was asked. When the older one finished last, a refresh restored an already-completed
   * round with its pre-success score and none of its served content: the same question again, and
   * its evidence written twice.
   *
   * A serial chain rather than a revision guard, deliberately. A guard would drop the stale write
   * but would still let round closure return before the checkpoint that preceded it had landed —
   * and "the round is closed" is only durable if everything the round already promised to write
   * is durable too. Chaining gets both properties from one rule: writes complete in enqueue order,
   * and awaiting the last one awaits all of them.
   *
   * The chain itself never holds a rejection (one failed write must not poison every later one),
   * but the caller still sees its own write's failure.
   */
  let snapshotWrites: Promise<unknown> = Promise.resolve();
  const enqueueSnapshotWrite = <T>(write: () => Promise<T>): Promise<T> => {
    const next = snapshotWrites.then(write);
    snapshotWrites = next.catch(() => {});
    return next;
  };

  /**
   * Miss settlement, one task at a time, for the whole sitting (R2-1).
   *
   * Two children can miss in the same tick, and each committed miss is one rung of the shared
   * ladder — serialising them is what stops both reading the same attempt number and spending one
   * rung between them.
   *
   * Runtime-scoped rather than per-listening-window, because a task queued during one window can
   * still be waiting when the next one opens (a ladder rung re-enters `awaitResponse` without the
   * round ending), and a queue that resets each time would forget it was owed anything.
   *
   * The chain the writes go through can only order writes it has been *given*. A miss task that
   * has not started yet has not enqueued its checkpoint, so awaiting the write chain says nothing
   * about it: closure would sail past, write "the round is over", and then be overwritten by a
   * checkpoint describing the round mid-ladder. So closure waits on the *tasks*, and only then
   * writes the snapshot that says the round is finished.
   *
   * Each task swallows its own failure exactly as the write chain does: one checkpoint that could
   * not be written must not stop the next miss being settled, and must not turn a round's closure
   * into a rejection (D-052(1)).
   */
  let missSettlements: Promise<void> = Promise.resolve();
  const enqueueMissSettlement = (settle: () => Promise<void>): void => {
    missSettlements = missSettlements.then(settle).catch(() => {});
  };

  /**
   * Resolves when nothing that could still enqueue a snapshot write is outstanding.
   *
   * Awaiting the tail is enough to await the whole queue, and the tail cannot move while it is
   * being awaited: this is only ever called once the round has settled, and a settled round
   * commits no further misses — after the first valid correct answer nobody can be marked wrong
   * at all (COOP-05), and every other way out of the round settles it first.
   */
  const settleOutstandingMisses = (): Promise<void> => missSettlements;

  const saveSnapshot = async (round: RoundCheckpoint): Promise<void> => {
    const { stageId, attemptNumber, onSimplified, contentId } = round;
    const state = score.getState();
    const snapshot: MissionProgressSnapshot = {
      sessionId: options.session.sessionId,
      gameId: options.session.gameId,
      missionId: options.session.missionId,
      playerMode: options.session.playerMode,
      inputMode: options.session.inputMode,
      players: options.players,
      stageId,
      roundIndex,
      attemptNumber,
      onSimplified,
      sawIncorrectAnswer: round.sawIncorrectAnswer,
      observedPlayerIds: rosterOrder(round.observedPlayerIds),
      ...(contentId === undefined ? {} : { contentId }),
      completedContentIds: [...completedContentIds],
      useCuratedSequence,
      skillPractice: analytics.skillPractice(),
      score: {
        teamScore: state.teamScore,
        roundsScored: state.roundsScored,
        participation: state.participation,
        appliedEventIds: state.appliedEventIds,
        ...(state.detail === undefined ? {} : { detail: state.detail }),
      },
      progress: analytics.counters(),
      // A set of children, not a history (R2-8): the one id array that outlives the sitting, so
      // an arrival order left in it — including one restored from an older snapshot — would be
      // the one ordering claim that survives a refresh. Canonicalised at the write, which is the
      // single point every restore path passes back through.
      teachingConsumedPlayerIds: rosterOrder(teaching.consumedPlayerIds()),
      startedAt: options.session.startedAt,
      updatedAt: clock().toISOString(),
    };

    try {
      await enqueueSnapshotWrite(() => options.repositories.missionProgress.saveProgress(snapshot));
    } catch {
      /**
       * Storage that will not take the checkpoint is a storage problem, never the child's (S1-5).
       *
       * The repository now *reports* a failed single-slot write instead of swallowing it, because
       * the shell has to be able to tell a grown-up that this sitting will not be resumable. What
       * must not happen is the report ending the round: a full disk is not a wrong answer, and
       * D-042(6)'s rule — a save failure never takes a finished mission away from a child —
       * applies with at least as much force to a mission still in progress.
       */
    }
  };

  /**
   * How long this challenge keeps listening after it has been won (COOP-03, D-009).
   *
   * Zero when there is nobody else in the room: the window exists so a second child's matching
   * action can be recorded as participation, and with one player it would only delay the next
   * question. That is what makes single player the degenerate case of this code path rather than
   * a differently-timed one.
   */
  const participationGraceMsFor = (
    stage: MissionStageDefinition,
    item: LearningContentItem,
  ): number => {
    if (options.players.length < 2) return 0;

    const policy = stage.cooperativePolicy;
    const expected = item.expectedResponse;
    const record =
      expected.recordSecondPlayerParticipation ?? policy?.recordSecondPlayerParticipation ?? true;
    if (!record) return 0;

    // The item's own window wins over the mechanic's (D-009): the boss stage is a zone/binary
    // mechanic that happens to contain one count-action round, and counting takes longer.
    const authored = expected.participationGraceMs ?? policy?.participationGraceMs;
    if (authored !== undefined) return Math.max(0, authored);

    return expected.mode === 'ACTION_COUNT'
      ? options.tunables.participationGraceMs.countAction
      : options.tunables.participationGraceMs.zoneOrBinary;
  };

  /**
   * Runs this round's listening.
   *
   * One candidate slot **per player** (D-045 PS2-2). A child arming an answer cannot cancel,
   * replace or reset the other child's — the single shared slot this replaced meant "first valid
   * correct wins" was really "last armed before the settle wins", and a sibling wandering into the
   * wrong portal deleted an answer somebody had already given.
   *
   * What ends the listening is deliberately short: the first valid correct answer, a ladder rung
   * that has to be taken outside the round, the 25 s step, a parent override, teardown. A wrong
   * answer is not on that list — it is written down, it advances the shared ladder, the retry goes
   * up, and the challenge stays open (PS2-1). `onMiss` is how the round loop does that work
   * without this function knowing anything about content, teaching or snapshots.
   */
  const awaitResponse = (
    stage: MissionStageDefinition,
    item: LearningContentItem,
    onMiss: MissHandler,
  ): Promise<RoundResponse> =>
    new Promise<RoundResponse>((resolve) => {
      const matcher = createResponseMatcher({
        expected: item.expectedResponse,
        tunables: options.tunables,
      });
      const graceMs = participationGraceMsFor(stage, item);

      let ladderTimers: RoundTimerId[] = [];
      /** One armed candidate per player. Never shared, never replaced by anybody else's. */
      const candidates = new Map<PlayerId, { timerId: RoundTimerId; correct: boolean }>();
      /**
       * The 25 s step, held separately so an answer already being validated can suspend it.
       *
       * A child who steps into the right portal at 24.5 s has answered; the deadline exists to
       * escalate the help offered to a child who has *not*. Letting it fire into an armed dwell
       * would cancel a deliberate correct response and hand that child a teaching moment for a
       * question they had just got right (R1-6). With two children it is held while *either* of
       * them is mid-answer, and it is never restarted while one of them still is.
       */
      let terminalTimer: RoundTimerId | null = null;
      let settled = false;
      /** Set the instant a correct answer wins. Nobody can be marked wrong after it (COOP-05). */
      let winner: PlayerId | null = null;
      let graceTimer: RoundTimerId | null = null;
      const graceParticipants: PlayerId[] = [];
      let closeGrace: (() => void) | null = null;
      /**
       * Whether the window has run out but is still waiting on an answer already in flight.
       *
       * The window says how long the other child has to *start*; a deadline that cut off a dwell
       * or a count already under way would be the same mistake the 25 s step used to make (R1-6),
       * and with a 900 ms count settle a 1,500 ms window would otherwise leave a child 600 ms to
       * charge the web. Bounded: nothing armed after the window expires is waited for.
       */
      let graceExpired = false;
      let unsubscribe: (() => void) | null = null;

      const cancelCandidate = (playerId: PlayerId): void => {
        const candidate = candidates.get(playerId);
        if (candidate === undefined) return;
        timers.cancel(candidate.timerId);
        candidates.delete(playerId);
      };

      const cancelAssistTimers = (): void => {
        for (const id of ladderTimers) timers.cancel(id);
        ladderTimers = [];
        if (terminalTimer !== null) {
          timers.cancel(terminalTimer);
          terminalTimer = null;
        }
      };

      /**
       * Everything armed, thrown away, with the round left listening (S1-4).
       *
       * The count a child has legitimately been credited with is **kept**: two jumps the running
       * mission saw are two confidently observed actions, and forgetting them would make a pause
       * punish a child for their grown-up answering the door. What is discarded is the *unfinished*
       * part — the dwell that had not run out, the settle that had not settled — because an
       * unfinished answer was never a confidently observed one (PS2-3).
       */
      const discardCandidates = (): void => {
        if (settled) return;
        const armed = candidates.size > 0;
        for (const playerId of [...candidates.keys()]) {
          cancelCandidate(playerId);
          /**
           * The onset that armed this candidate goes with it (S2-4).
           *
           * Round-1's ruling stands — counts the running mission genuinely saw are kept — but the
           * *final* count and the candidate are one movement seen from two ends. Keeping its count
           * while discarding its settle left a child standing at the target with nothing to show
           * for it, and the repeat they naturally make overshot into a wrong answer. A matcher that
           * keeps no history (a lane, a posture) defines nothing and is left alone.
           */
          matcher.rollbackLastObservation?.(playerId);
        }
        // Arming suspends the 25 s step so a deliberate answer is never cut off; with the answer
        // gone the step is owed back, or a discarded dwell would leave the round with no way on.
        if (armed) armTerminalTimer();
      };

      const teardown = (): void => {
        discardArmedAnswers = null;
        cancelAssistTimers();
        for (const playerId of [...candidates.keys()]) cancelCandidate(playerId);
        if (graceTimer !== null) {
          timers.cancel(graceTimer);
          graceTimer = null;
        }
        unsubscribe?.();
      };

      const finish = (response: RoundResponse): void => {
        if (settled) return;
        settled = true;
        abortRound = null;
        teardown();
        resolve(response);
      };

      /** The first valid correct answer. Everything after this is participation or nothing. */
      const lockCorrect = (playerId: PlayerId): void => {
        if (settled) return;
        settled = true;
        winner = playerId;
        cancelAssistTimers();
        for (const [otherId, candidate] of [...candidates]) {
          // An unfinished *correct* dwell may still land inside the window below as participation
          // (never as a second score, never as evidence). An unfinished wrong one is discarded
          // without evidence: an unfinished dwell was never a confidently observed answer (PS2-3).
          if (otherId !== playerId && candidate.correct) continue;
          cancelCandidate(otherId);
        }

        const participants = new Promise<readonly PlayerId[]>((resolveParticipants) => {
          closeGrace = (): void => {
            closeGrace = null;
            abortRound = null;
            teardown();
            resolveParticipants([...graceParticipants]);
          };
          if (graceMs <= 0) {
            closeGrace();
            return;
          }
          graceTimer = timers.after(graceMs, () => {
            graceTimer = null;
            graceExpired = true;
            // An answer the other child is already giving finishes; nothing new may start.
            if (candidates.size === 0) closeGrace?.();
          });
        });

        /**
         * While the window is open the round is already won, so the only commands that need to
         * reach it are the ones that stop the sitting. A Skip or a Simplify pressed in this
         * sub-second gap is queued exactly as one pressed between rounds is (R1-5) — it cannot
         * un-win the question the child has just answered.
         */
        abortRound = (response: RoundResponse): void => {
          if (response.kind === 'ABORTED') {
            closeGrace?.();
            return;
          }
          if (response.kind !== 'OVERRIDE') return;
          if (response.command.type === 'END_SESSION') {
            closeGrace?.();
            return;
          }
          pendingOverride = response.command;
        };

        resolve({ kind: 'CORRECT', playerId, graceParticipants: participants });
      };

      const recordParticipation = (playerId: PlayerId): void => {
        if (playerId === winner) return;
        if (graceParticipants.includes(playerId)) return;
        graceParticipants.push(playerId);
      };

      /** A miss the child has actually made: written down, then the round carries on listening. */
      const commitMiss = (playerId: PlayerId): void => {
        // Written down here and now, before any other timer in this tick can read the ladder.
        const settleMiss = onMiss(playerId);
        enqueueMissSettlement(async () => {
          const outcome = await settleMiss(() => !settled);
          // The other child may have won the round while this miss was being written down. Their
          // answer stands; this rung does not.
          if (settled) return;
          if (outcome.disposition === 'STOP') {
            finish({ kind: 'LADDER_STEP', step: outcome.step });
            return;
          }
          restartAssistTimers();
        });
      };

      const commit = (playerId: PlayerId, correct: boolean): void => {
        if (settled) {
          // After the first valid correct: a matching answer inside the window is participation,
          // and nothing at all is a wrong answer (COOP-05).
          if (winner !== null && correct) recordParticipation(playerId);
          if (graceExpired && candidates.size === 0) closeGrace?.();
          return;
        }
        matcher.reset(playerId);
        if (correct) {
          lockCorrect(playerId);
          return;
        }
        commitMiss(playerId);
      };

      const arm = (playerId: PlayerId, correct: boolean, commitAfterMs: number): void => {
        // Only this child's own later action replaces this child's candidate.
        cancelCandidate(playerId);
        // Nothing a child does after the round is won can be wrong, so there is nothing to hold.
        if (settled && !correct) {
          if (graceExpired && candidates.size === 0) closeGrace?.();
          return;
        }
        // The window has run out: an answer that had not started by then does not begin now.
        if (graceExpired) {
          if (candidates.size === 0) closeGrace?.();
          return;
        }
        if (!settled && terminalTimer !== null) {
          timers.cancel(terminalTimer);
          terminalTimer = null;
        }
        const timerId = timers.after(commitAfterMs, () => {
          candidates.delete(playerId);
          commit(playerId, correct);
        });
        candidates.set(playerId, { timerId, correct });
      };

      abortRound = finish;
      discardArmedAnswers = discardCandidates;

      unsubscribe = options.input.subscribe((event: ThriveInputEvent) => {
        // A pause is not a moment in which a child can answer; it is also when the browser
        // delivers the retraction events a blur produces.
        if (!isRunning()) return;

        const verdict: MatcherVerdict = matcher.observe(event);
        if (verdict.kind === 'IGNORED') return;

        if (verdict.kind === 'RESOLVED') {
          commit(verdict.playerId, verdict.correct);
          return;
        }

        arm(verdict.playerId, verdict.correct, verdict.commitAfterMs);
      });

      const { timersMs } = options.tunables;

      /**
       * The assist ladder for the attempt now on screen.
       *
       * Restarted when a retry goes up, which is what re-entering this function used to do. The
       * 25 s step is the exception: it is not restarted while another child is still mid-answer,
       * or one child's miss would resurrect a deadline that cuts the other one off (R1-6).
       */
      function restartAssistTimers(): void {
        cancelAssistTimers();

        ladderTimers.push(
          timers.after(timersMs.repeatInstruction, () => {
            options.onEvent({
              type: 'ROUND_ASSIST',
              roundIndex,
              assist: 'REPEAT_INSTRUCTION',
              text: item.prompt.spokenInstruction,
            });
            speak(item.prompt.spokenInstruction);
          }),
        );

        const hint = item.hints[0];
        ladderTimers.push(
          timers.after(timersMs.visualHint, () => {
            const text = hint?.spokenText ?? hint?.text;
            options.onEvent({
              type: 'ROUND_ASSIST',
              roundIndex,
              assist: 'VISUAL_HINT',
              ...(text === undefined ? {} : { text }),
            });
            if (hint?.spokenText !== undefined) speak(hint.spokenText);
          }),
        );

        armTerminalTimer();
      }

      /** The 25 s step, on its own, so a discarded answer can put it back without restarting the ladder. */
      function armTerminalTimer(): void {
        if (settled || candidates.size > 0 || terminalTimer !== null) return;
        terminalTimer = timers.after(timersMs.teachingOrOverride, () => {
          terminalTimer = null;
          finish({ kind: 'TIMEOUT' });
        });
      }

      restartAssistTimers();
    });

  /** The easier variant of this curated position (D-034 PS-1), or nothing. */
  const simplerVariant = async (item: LearningContentItem): Promise<LearningContentItem | null> => {
    const band = lowerBand(item.difficultyBand);

    if (item.slotId !== undefined && band !== null) {
      // Slot retrieval may return fewer items than asked for, so never index positionally into
      // an assumed one-to-one result (D-035).
      const results = await options.content.getContent(
        { slotIds: [item.slotId], band },
        selectionContext(),
      );
      const variant = results.find((candidate) => candidate.id !== item.id);
      if (variant !== undefined) return variant;
    }

    if (item.fallbackRef !== undefined) {
      const results = await options.content.getContent(
        { contentIds: [item.fallbackRef] },
        selectionContext(),
      );
      const variant = results.find((candidate) => candidate.id === item.fallbackRef);
      if (variant !== undefined && variant.id !== item.id) return variant;
    }

    return null;
  };

  const hasSimplerCandidate = (item: LearningContentItem): boolean =>
    item.fallbackRef !== undefined ||
    (item.slotId !== undefined && lowerBand(item.difficultyBand) !== null);

  /**
   * Whether two items are answered by the same physical action (R2-2).
   *
   * A stage is one mechanic and one scene: the Spider-Sense Factory draws "hands up for yes,
   * crouch down for no" and nothing else, and the keyboard context follows the item that is
   * open. So an item swapped in mid-round that expects a *different* action puts a child in
   * front of instructions that cannot answer the question in front of them — the one failure
   * mode where following the visible rules correctly still gets you nowhere. The response mode
   * is what the platform matches on and the input method is what the scene draws from; both
   * have to agree, because a mismatch in either one is a mismatch the child can see.
   */
  const answersTheSameWay = (
    candidate: LearningContentItem,
    current: LearningContentItem,
  ): boolean =>
    candidate.expectedResponse.mode === current.expectedResponse.mode &&
    candidate.prompt.inputMethod === current.prompt.inputMethod;

  /** One item by id, or nothing. Used for the authored post-teaching confirmation. */
  const itemById = async (contentId: string): Promise<LearningContentItem | null> => {
    const results = await options.content.getContent(
      { contentIds: [contentId] },
      selectionContext(),
    );
    return results.find((candidate) => candidate.id === contentId) ?? null;
  };

  /**
   * What the child is asked immediately after a teaching moment (R1-2, MVP notes §11).
   *
   * In order: the moment's own authored confirmation question, then the easier variant of this
   * position, then — at Explorer, or on a replay item with no curated position — this same
   * question again. The last case is not a fourth full-difficulty attempt: it is the
   * post-teaching rung, so it scores the post-teaching tier and the ladder terminates on it. A
   * teaching moment that ends by re-asking the question the child has already missed twice, at
   * full difficulty, and only relents after a *fourth* interaction, is not teaching.
   */
  const postTeachingAsk = async (
    item: LearningContentItem,
    definition: TeachingMomentDefinition | undefined,
  ): Promise<LearningContentItem> => {
    const confirmationId = definition?.confirmationContentId;
    if (confirmationId !== undefined && confirmationId !== item.id) {
      const confirmation = await itemById(confirmationId);
      if (
        confirmation !== null &&
        isSupportedResponseMode(confirmation.expectedResponse) &&
        answersTheSameWay(confirmation, item)
      ) {
        return confirmation;
      }
    }

    const simpler = await simplerVariant(item);
    // A slot's variants share a mechanic by construction, but `fallbackRef` is authored freely,
    // so the same guard applies to the rung below as to the confirmation above.
    return simpler !== null && answersTheSameWay(simpler, item) ? simpler : item;
  };

  /**
   * Writes what a round taught us, immediately.
   *
   * Evidence used to be handed to the repository only at mission end, which meant a tablet closed
   * on round twelve threw away twelve rounds of it. A round is the unit of learning, so it is
   * also the unit of durability.
   */
  /**
   * How much of the analytics buffer has already reached storage (S1-9, S3-1).
   *
   * A cursor on the runtime rather than an index each caller remembers, because two callers can be
   * in flight at once: a miss settlement persisting its own row while the round's closure persists
   * everything the round recorded. Each had captured its own "from" before the other ran, so their
   * slices overlapped and the same row was written twice.
   *
   * It means "written", not "claimed", and it is advanced only once the write has actually
   * succeeded. Advancing it before the await did stop the double write, but it turned a transient
   * rejection into permanent loss: the settlement swallows its own failure exactly as it must
   * (D-052(1)), closure then found nothing fresh to write, and a child's wrong answer and another
   * child's right one were both simply gone. What makes "advance after success" safe against the
   * overlap is the chain below, not the timing of the increment.
   */
  let persistedEvidenceCount = 0;

  /**
   * One ordered chain for every evidence write of the sitting (the D-052(1)/D-053(1) pattern).
   *
   * Serialising is what makes overlap impossible, so the slice can be taken *inside* the chain
   * against a cursor that is always current, and a failed write can simply leave its rows for the
   * next call to pick up. The chain never holds a rejection — one failed write must not poison
   * every later one — while the caller still sees its own.
   */
  let evidenceWrites: Promise<void> = Promise.resolve();

  const persistRoundEvidence = (): Promise<void> => {
    const next = evidenceWrites.then(() => writeFreshEvidence());
    evidenceWrites = next.catch(() => {});
    return next;
  };

  const writeFreshEvidence = async (): Promise<void> => {
    const fresh = analytics.evidence().slice(persistedEvidenceCount);
    if (fresh.length === 0) return;

    /**
     * Session identity in, durable identity out (R1-3).
     *
     * Everything upstream of this line — the analytics buffer, the participation counts, the
     * finished session the parent summary is built from — speaks in lanes, because within one
     * sitting that is exactly right. This is the boundary where a row stops describing a seat and
     * starts describing a child, so it is where the two identities are reconciled: a named child's
     * rows are rewritten to their profile, and a guest's are not written at all.
     */
    const durable = fresh.flatMap((entry) => {
      const subjectId = subjectFor(entry.playerId);
      return subjectId === null ? [] : [{ ...entry, playerId: subjectId }];
    });
    /**
     * Nothing durable in this slice — a guest's rows, which are never written (D-052(3)).
     *
     * The cursor deliberately does *not* move: it means "written", and nothing was. A later call
     * simply re-slices the same guest rows, filters them out again, and writes whatever named
     * rows have arrived behind them. Advancing here instead was an unkillable belt, so it was
     * removed rather than shipped (the D-044(2) rule).
     */
    if (durable.length === 0) return;

    await options.repositories.skills.recordEvidence(durable);
    // Only now: until the repository has taken them, these rows have not been written and the
    // next call must still be able to find them.
    persistedEvidenceCount += fresh.length;
  };

  const recordRound = (
    stage: MissionStageDefinition,
    item: LearningContentItem,
    outcome: {
      completed: boolean;
      attemptNumber: number;
      teachingUsed: boolean;
      onSimplified: boolean;
      sawIncorrectAnswer: boolean;
      observedPlayerIds: PlayerId[];
      durationMs: number;
      scriptedTeachingDelivery?: 'FULL' | 'HINT' | 'NONE';
      /** Who committed a wrong answer of their own this round (PS2-3). */
      missedPlayerIds?: readonly PlayerId[];
      /**
       * Players this round only knows about from the resume snapshot (R1-2).
       *
       * Private, like `completedByPlayerId`: it decides whose evidence is what and reaches no
       * event, outcome, snapshot or analytics record. A player stays on this list until they
       * answer again in this sitting.
       */
      restoredObserverPlayerIds?: readonly PlayerId[];
      /**
       * Whose correct answer completed it.
       *
       * A private argument, deliberately: it decides whose evidence is what, and it is written to
       * no event, outcome, snapshot or analytics record. There is no winner anywhere downstream —
       * the score is the team's and participation is a neutral count (architecture §12).
       */
      completedByPlayerId?: PlayerId;
      /** Who matched inside the participation grace window (COOP-03). */
      graceParticipantIds?: readonly PlayerId[];
      /**
       * Whose own `incorrect` this round has committed to writing durably, at the moment they gave
       * it (S1-9). Membership is claimed when the miss happens, so it is true from the instant the
       * answer is given rather than from whenever the settlement task gets to run.
       *
       * Private like the two above. It exists so the round's closing evidence does not write the
       * same wrong answer a second time — the miss row and the close row would be one child, one
       * round, one mistake, counted twice.
       */
      durableMissPlayerIds?: readonly PlayerId[];
    },
  ): void => {
    analytics.recordRoundOutcome({
      stageId: stage.id,
      contentId: item.id,
      skillIds: item.skillIds,
      attemptNumber: outcome.attemptNumber,
      completed: outcome.completed,
      teachingUsed: outcome.teachingUsed,
      observedPlayerIds: rosterOrder(outcome.observedPlayerIds),
      durationMs: outcome.durationMs,
      ...(outcome.scriptedTeachingDelivery === undefined
        ? {}
        : { scriptedTeachingDelivery: outcome.scriptedTeachingDelivery }),
      ...(outcome.graceParticipantIds === undefined || outcome.graceParticipantIds.length === 0
        ? {}
        : { graceParticipantIds: rosterOrder(outcome.graceParticipantIds) }),
    });

    /**
     * Nobody answered. A non-response is `skipped`, never `incorrect` (architecture §12).
     *
     * The observed players are part of that test, not just the flag (R3-2). `incorrect` names a
     * child who gave a wrong answer, so a round that ends unfinished with nobody recorded as
     * having answered cannot produce one — there is nobody for it to be about. The runtime used
     * to fall back to "every player in the session" here, which turned one incoherent pair of
     * restored fields into a wrong answer against a sibling who never touched a key.
     */
    if (
      !outcome.completed &&
      (!outcome.sawIncorrectAnswer || outcome.observedPlayerIds.length === 0)
    ) {
      analytics.recordNonResponse({
        playerIds: options.players.map((player) => player.playerId),
        skillIds: item.skillIds,
        attemptNumber: outcome.attemptNumber,
        teachingUsed: outcome.teachingUsed,
      });
      return;
    }

    const roundResult: SkillEvidenceResult = outcome.completed
      ? outcome.teachingUsed || outcome.onSimplified
        ? 'assisted'
        : 'correct'
      : 'incorrect';

    /**
     * What this round taught us about *this* child (PS2-3).
     *
     * A round the team finished is a round the team finished, and the shard tier already carries
     * how much help it took — so a child who missed and then got it right themselves is recorded
     * by their own answer, exactly as single player always has. What changes with two children is
     * that a wrong answer somebody else's correct answer arrived after is still that child's own
     * wrong answer, and is not overwritten by a success they did not give.
     */
    const missed = new Set(outcome.missedPlayerIds ?? []);
    const resultFor = (playerId: PlayerId): SkillEvidenceResult =>
      outcome.completed && playerId !== outcome.completedByPlayerId && missed.has(playerId)
        ? 'incorrect'
        : roundResult;

    /**
     * Who the evidence is about.
     *
     * An unfinished round has already returned above unless somebody was observed, so this
     * fallback is reachable only for a *completed* one — a round the team finished, credited to
     * the team. It is deliberately not reachable for `incorrect` (R3-2).
     *
     * **A restored observer is not credited with a success they were not here for (R1-2).** The
     * snapshot says the round had seen a wrong answer and who was observed; it deliberately does
     * not say which child was wrong (D-047(4)), and that child's own answer was already written
     * down, durably, at the moment they gave it (D-036(3)). So on a round the team *completes*,
     * evidence goes to the children who responded in this sitting; a child whose only observed
     * response predates the resume, and who has not answered since, keeps what their own answer
     * recorded and gains nothing from somebody else's correct one. Team credit for a completed
     * round survives everywhere it does not contradict a child's own recorded response
     * (D-044(3)).
     *
     * Unfinished rounds are untouched: a restored observer still keeps the round out of "nobody
     * answered" and still records their own `incorrect` when a grown-up skips it (R2-3,
     * D-041(4)) — that repeats the child's own answer rather than inventing a different one.
     */
    const restoredOnly = new Set(outcome.restoredObserverPlayerIds ?? []);
    const observedThisSitting = outcome.observedPlayerIds.filter(
      (playerId) => !restoredOnly.has(playerId),
    );
    const credited = outcome.completed ? observedThisSitting : outcome.observedPlayerIds;

    const players = rosterOrder(
      credited.length > 0 ? credited : options.players.map((player) => player.playerId),
    );

    /**
     * A wrong answer is written down once (S1-9).
     *
     * A child whose miss was already made durable at the moment they gave it is not recorded again
     * here — the row exists, and it says exactly what this one would. Only a row that would repeat
     * the miss is dropped: the child who went on to answer correctly still gets their completion
     * row, because that is a different thing that happened.
     */
    const recordedByTheMissPath = new Set(outcome.durableMissPlayerIds ?? []);
    const toRecord = players.filter(
      (playerId) => !(recordedByTheMissPath.has(playerId) && resultFor(playerId) === 'incorrect'),
    );

    const recordedAt = clock().toISOString();
    analytics.recordEvidence(
      toRecord.flatMap((playerId) =>
        item.skillIds.map((skillId) =>
          createSkillEvidence({
            playerId,
            gameId: options.gameId,
            missionId: options.mission.id,
            skillId,
            result: resultFor(playerId),
            attemptNumber: outcome.attemptNumber,
            teachingUsed: outcome.teachingUsed,
            recordedAt,
          }),
        ),
      ),
    );
  };

  /** One scored round, including every step of its help ladder. */
  const runRound = async (
    stage: MissionStageDefinition,
    original: LearningContentItem,
  ): Promise<void> => {
    const startedMs = scheduler.now();
    // The module's own policy gets to choose a safer band for this position before it is asked
    // (R1-13). It cannot choose a harder one, and nothing about the choice is said to the child.
    let item = await adaptItem(stage, original);
    let attemptNumber = 1;
    let teachingUsed = false;
    let onSimplified = false;
    let sawIncorrectAnswer = false;
    const observed = new Set<PlayerId>();
    /**
     * Who committed a wrong answer of their own (PS2-3).
     *
     * Not carried in the resume snapshot on purpose (GOVERNED_RECORDS, D-033): a round restored
     * mid-way knows *that* it saw a wrong answer and who was observed, not which of two children
     * gave it. The attribution therefore degrades downwards after a refresh — a miss can be
     * forgotten, never invented against a child.
     */
    const missed = new Set<PlayerId>();
    /**
     * Whose own wrong answer this round has already written to durable storage (S1-9).
     *
     * D-052(2) says a restored observer "keeps what their own answer already wrote durably at miss
     * time (D-036(3))" — that is the whole reason it is safe for a completed round to credit only
     * the children who answered in *this* sitting. The premise was not true: evidence was written
     * when the round closed, so a miss followed by a reload and somebody else's correct answer
     * left the child who was actually wrong with nothing at all recorded. The miss is made durable
     * where it happens instead, which is also where the checkpoint already is.
     */
    const durableMisses = new Set<PlayerId>();
    /**
     * Who this round only knows about from the snapshot it resumed (R1-2).
     *
     * A child leaves this set the moment they answer again in this sitting, at which point they
     * are an ordinary responder with an ordinary record.
     */
    const restoredObservers = new Set<PlayerId>();
    /** Whether the ladder's simplified rung was asked for by a grown-up rather than reached. */
    let simplifyRequestedByParent = false;

    // The interrupted round this sitting is carrying on (PRO-03, R1-3). Consumed once: only the
    // round the snapshot describes resumes mid-way; every round after it starts fresh.
    const resumed = resumeInto;
    resumeInto = null;
    if (resumed !== null) {
      attemptNumber = Math.max(1, resumed.attemptNumber);
      onSimplified = resumed.onSimplified;
      // What the round had already seen (R2-3). Without it, a grown-up's Skip after a refresh
      // recorded a non-response for a question the child had genuinely answered — turning an
      // `incorrect` the child earned into a `skipped` nobody did (D-041(4)).
      sawIncorrectAnswer = resumed.sawIncorrectAnswer;
      for (const playerId of resumed.observedPlayerIds) {
        observed.add(playerId);
        restoredObservers.add(playerId);
      }
      if (resumed.contentId !== undefined && resumed.contentId !== original.id) {
        const restoredItem = await itemById(resumed.contentId);
        // A question that can no longer be resolved falls back to the position's own item rather
        // than opening a blank round; the attempt state the child earned is still honoured.
        if (restoredItem !== null) item = restoredItem;
      }
    }
    /** How much of the item's proactive teaching ran, if it asked for one (D-034 PS-5). */
    let scriptedTeachingDelivery: 'FULL' | 'HINT' | 'NONE' | undefined;

    const resolveRound = async (
      completed: boolean,
      shardsAwarded: number,
      moveOnReason?: RoundMoveOnReason,
      cooperative?: { completedByPlayerId: PlayerId; graceParticipantIds: readonly PlayerId[] },
    ): Promise<void> => {
      recordRound(stage, item, {
        completed,
        attemptNumber,
        teachingUsed,
        onSimplified,
        sawIncorrectAnswer,
        observedPlayerIds: [...observed],
        missedPlayerIds: [...missed],
        durationMs: Math.max(0, scheduler.now() - startedMs),
        restoredObserverPlayerIds: [...restoredObservers],
        durableMissPlayerIds: [...durableMisses],
        ...(scriptedTeachingDelivery === undefined ? {} : { scriptedTeachingDelivery }),
        ...(cooperative === undefined
          ? {}
          : {
              completedByPlayerId: cooperative.completedByPlayerId,
              graceParticipantIds: cooperative.graceParticipantIds,
            }),
      });

      completedContentIds.push(original.id);
      // Whatever the child was actually asked, whichever route chose it — an adapted band, a
      // simplified fallback, a post-teaching confirmation — is served content too.
      if (item.id !== original.id) completedContentIds.push(item.id);

      options.onEvent({
        type: 'ROUND_RESOLVED',
        roundIndex,
        stageId: stage.id,
        contentId: item.id,
        completed,
        attemptNumber,
        teachingUsed,
        shardsAwarded,
        observedPlayerIds: rosterOrder(observed),
        ...(moveOnReason === undefined ? {} : { moveOnReason }),
      });

      /**
       * Nothing may still be owed a snapshot write when the round says it is finished (R2-1).
       *
       * Awaited *after* the event rather than before it: the child's celebration is not held
       * behind storage, exactly as the shard award is not. A checkpoint drained here still
       * describes the round as it was — this round's index, mid-ladder — because the closing
       * write that supersedes it is only enqueued below.
       */
      await settleOutstandingMisses();

      roundIndex += 1;
      await persistRoundEvidence();
      // The round is over, so the next one starts from nothing observed.
      await saveSnapshot({
        stageId: stage.id,
        attemptNumber: 1,
        onSimplified: false,
        sawIncorrectAnswer: false,
        observedPlayerIds: [],
      });
    };

    /**
     * Writes down where the round is before the next state reaches the child (R1-3).
     *
     * Every transition — an attempt spent, a teaching cap spent, a fall back to the simpler
     * question — is durable at the moment it happens rather than at the end of the round, because
     * the interruption this snapshot exists for is precisely the one that stops the round ending.
     */
    const checkpoint = async (): Promise<void> => {
      await saveSnapshot({
        stageId: stage.id,
        attemptNumber,
        onSimplified,
        sawIncorrectAnswer,
        observedPlayerIds: [...observed],
        ...(item.id === original.id ? {} : { contentId: item.id }),
      });
    };

    /** Where the round is on the help ladder, right now. Read fresh at every decision. */
    const ladderContext = (): LadderContext => ({
      attemptNumber,
      teachingUsed,
      teachingAvailable:
        stage.teachingEnabled &&
        item.teachingRef !== undefined &&
        options.players.some((player) => teaching.isAvailableFor(player.playerId)),
      simplifiedAvailable: hasSimplerCandidate(item),
      onSimplified,
    });

    /** Puts the question in front of the children — a first ask or a retry, identically. */
    const present = (): void => {
      options.onEvent({
        type: 'ROUND_PRESENTED',
        roundIndex,
        stageId: stage.id,
        contentId: item.id,
        attemptNumber,
        simplified: onSimplified,
        prompt: promptOf(item),
        // Passed through, never interpreted: what a game declares about its own content is the
        // game's business, and the alternative is a scene reading meaning out of a content id.
        ...(item.metadata === undefined ? {} : { metadata: item.metadata }),
      });
      currentSpokenInstruction = item.prompt.spokenInstruction;
      speak(item.prompt.spokenInstruction);
    };

    /**
     * One child's wrong answer, written down while the round goes on listening (PS2-1).
     *
     * It records *that* child's miss, spends one rung of the shared team ladder, and — when the
     * rung is a retry — puts the question straight back up. What it must never do is end the
     * challenge: the other child may be halfway through an answer of their own, and in single
     * player this is the same sequence of events the resolve-and-re-ask loop used to produce.
     */
    /**
     * The child's own wrong answer, made durable at the moment they gave it (S1-9, D-036(3)).
     *
     * Recorded into the analytics buffer *and* pushed to storage here rather than at round close,
     * because the interruption this exists for is precisely the one that stops the round closing.
     * A guest writes nothing, exactly as everywhere else (`persistRoundEvidence` applies
     * `learningSubjectIdOf`, D-052(3)), and `durableMisses` is what stops the closing write
     * repeating the row.
     *
     * Ordering: this runs inside the miss *settlement* task, after that task's checkpoint, so it
     * joins the serialised chain (D-052(1)) and closure — which waits on the tasks after
     * `ROUND_RESOLVED` is already out (D-053(1)) — is never held behind storage.
     */
    const commitMissEvidence = async (playerId: PlayerId, attempt: number): Promise<void> => {
      const recordedAt = clock().toISOString();
      analytics.recordEvidence(
        item.skillIds.map((skillId) =>
          createSkillEvidence({
            playerId,
            gameId: options.gameId,
            missionId: options.mission.id,
            skillId,
            result: 'incorrect',
            attemptNumber: attempt,
            teachingUsed,
            recordedAt,
          }),
        ),
      );
      await persistRoundEvidence();
    };

    const handleMiss: MissHandler = (playerId: PlayerId) => {
      observed.add(playerId);
      // They have answered in this sitting, so nothing about them is second-hand any more (R1-2).
      restoredObservers.delete(playerId);
      missed.add(playerId);
      sawIncorrectAnswer = true;
      options.onEvent({
        type: 'RESPONSE_OBSERVED',
        roundIndex,
        playerId,
        correct: false,
        attemptNumber,
      });
      const step = nextStepAfterMiss(ladderContext());
      // The attempt they were answering, captured before the ladder advances: the evidence should
      // say which try this was, not what the round went on to become.
      const attemptAtMiss = attemptNumber;
      /**
       * Claimed here, synchronously, not when the settlement task eventually runs (S1-9 round 2).
       *
       * The set answers "is this child's own wrong answer accounted for by the miss path?", and
       * the answer is yes the instant they give it — the settlement task is already queued. Read
       * as "has it been written yet?", closure could arrive first (both children resolving inside
       * one scheduler advance with a zero-length grace window), be told no, and write the row a
       * second time.
       */
      durableMisses.add(playerId);
      // Only a child's answer advances the attempt count.
      attemptNumber += 1;

      return async (stillOpen: () => boolean): Promise<MissDisposition> => {
        // Durable before the retry reaches the child: a refresh here used to hand back a fresh
        // first attempt, which inflates both the shard tier and the evidence (R1-3).
        await checkpoint();
        // And the child's own answer is durable too, not only the round's position (S1-9).
        await commitMissEvidence(playerId, attemptAtMiss);

        // The other child answered correctly while this miss was being written down. Their answer
        // stands, this rung is dropped, and no retry is put up over a question already answered.
        if (!stillOpen()) return { disposition: 'CONTINUE' };
        if (step !== 'RETRY') return { disposition: 'STOP', step };

        present();
        return { disposition: 'CONTINUE' };
      };
    };

    if (!isSupportedResponseMode(item.expectedResponse)) {
      /**
       * A question this build cannot resolve is a content/capability mismatch, not something the
       * child did (R1-17). It is recorded as a diagnostic and the mission moves on: no academic
       * evidence, and no "questions attempted" for a question nobody was ever shown.
       */
      analytics.recordContentDiagnostic({
        contentId: item.id,
        code: 'UNPLAYABLE_RESPONSE_MODE',
      });

      completedContentIds.push(original.id);
      options.onEvent({
        type: 'ROUND_RESOLVED',
        roundIndex,
        stageId: stage.id,
        contentId: item.id,
        completed: false,
        attemptNumber,
        teachingUsed: false,
        shardsAwarded: 0,
        observedPlayerIds: [],
        moveOnReason: 'UNPLAYABLE_CONTENT',
      });
      roundIndex += 1;
      await saveSnapshot({
        stageId: stage.id,
        attemptNumber: 1,
        onSimplified: false,
        sawIncorrectAnswer: false,
        observedPlayerIds: [],
      });
      return;
    }

    /**
     * The proactive, content-declared teaching moment (D-034 PS-5, recon C-2).
     *
     * It runs *before* the round is presented, which is what "teach, then test" means: the child
     * has not been asked anything yet, so there is no attempt to spend, no answer to be wrong,
     * and no timer ladder to hold back — the round's timers only exist inside `awaitResponse`,
     * and the round ladder is suspended around this anyway so a later caller cannot change that
     * by accident. It goes through the same controller as the ladder's reactive moment, so the
     * one-full-moment-per-player cap and its degradation to a hint hold by construction rather
     * than by this call site remembering to check them.
     */
    if (original.scriptedTeachingRef !== undefined && stage.teachingEnabled) {
      const wasPaused = timers.paused;
      if (!wasPaused) timers.pause();
      const scripted = await teaching.deliver(
        original.scriptedTeachingRef,
        {
          playerId: options.players[0]?.playerId ?? '',
          skillIds: original.skillIds,
          band: teamBandOf(options.players),
          attemptNumber: 1,
        },
        // The cap this moment spends is durable *before* the moment starts playing (R1-3): the
        // interruption this snapshot exists for is precisely one that lands mid-moment.
        { trigger: 'SCRIPTED', onCapSpent: checkpoint },
      );
      scriptedTeachingDelivery = scripted.delivery;
      // Only if the mission itself is still running: a parent who paused *during* the teaching
      // must not have the round ladder restarted underneath them by this restore.
      if (!wasPaused && isRunning()) timers.resume();
      if (isDisposed()) return;
    }

    for (;;) {
      // Nothing is waiting for an answer while a teaching moment runs, so an `END_SESSION` that
      // lands during one has no round to interrupt. Checked here as well as between rounds, or
      // the mission would open the next question after a grown-up had ended the sitting.
      if (isDisposed() || endSessionRequested) return;

      /**
       * An override pressed while a teaching moment held the round (R1-5).
       *
       * The pause overlay is the only place these buttons exist, and a paused teaching moment has
       * no round waiting for an answer — so the command was queued rather than lost, and this is
       * where it takes effect: at the next point the round would otherwise be presented.
       */
      if (pendingOverride !== null) {
        const queued = pendingOverride;
        pendingOverride = null;
        if (queued.type === 'SKIP_ROUND') {
          await resolveRound(false, 0, 'PARENT_OVERRIDE');
          return;
        }
        if (queued.type === 'SIMPLIFY') {
          const simpler = await simplerVariant(item);
          if (simpler === null) {
            // Nothing easier exists (R1-5 reopen). The queued command is the same request as the
            // in-round one, so it gets the same answer: the mission moves on, and the reason says
            // a grown-up asked — never `LADDER_EXHAUSTED`, which would blame the child for a
            // ladder they never climbed, and never a silent re-ask of the identical question.
            await resolveRound(false, 0, 'PARENT_OVERRIDE');
            return;
          }
          item = simpler;
          onSimplified = true;
          await checkpoint();
        }
      }

      present();

      const response = await awaitResponse(stage, item, handleMiss);
      if (isDisposed() || response.kind === 'ABORTED') return;

      if (response.kind === 'CORRECT') {
        observed.add(response.playerId);
        restoredObservers.delete(response.playerId);
        options.onEvent({
          type: 'RESPONSE_OBSERVED',
          roundIndex,
          playerId: response.playerId,
          correct: true,
          attemptNumber,
        });

        const eventType = scoringEventTypeFor(ladderContext());
        const before = score.getState().teamScore;
        score.award({
          // One id per round, so both children answering inside the grace window — and any
          // redelivery — score exactly once (D-009).
          eventId: `${options.session.sessionId}.round-${roundIndex}`,
          type: eventType,
          contentId: item.id,
          stageId: stage.id,
          contributingPlayerIds: rosterOrder(observed),
          at: scheduler.now(),
        });
        const shardsAwarded = score.getState().teamScore - before;

        // The child's success has already happened — the shard is awarded and the event is out.
        // What is waited for here is only the other child's chance to join in (COOP-03); with one
        // player the window is zero-length and this resolves in the same turn.
        const graceParticipantIds = await response.graceParticipants;
        if (isDisposed()) return;

        await resolveRound(true, shardsAwarded, undefined, {
          completedByPlayerId: response.playerId,
          graceParticipantIds,
        });
        return;
      }

      let step;
      if (response.kind === 'LADDER_STEP') {
        step = response.step;
      } else if (response.kind === 'OVERRIDE') {
        if (response.command.type === 'SIMPLIFY') {
          step = 'SIMPLIFIED' as const;
          simplifyRequestedByParent = true;
        } else {
          await resolveRound(false, 0, 'PARENT_OVERRIDE');
          return;
        }
      } else {
        // TIMEOUT. Nothing was answered, so nothing is marked wrong and no attempt is spent.
        step = nextStepAfterTimeout(ladderContext());
      }

      // A retry never comes back out here: it is taken inside the round's own listening, so the
      // challenge stays open for the other child while it happens (PS2-1). Neither
      // `nextStepAfterTimeout` nor an override can produce one.
      if (step === 'RETRY') continue;

      if (step === 'TEACHING') {
        const delivery = await teaching.deliver(
          item.teachingRef ?? '',
          {
            playerId: options.players[0]?.playerId ?? '',
            skillIds: item.skillIds,
            band: teamBandOf(options.players),
            attemptNumber,
          },
          { onCapSpent: checkpoint },
        );
        // A moment that could not be delivered is not teaching, and must not close the door on
        // the simplified step behind it.
        teachingUsed = delivery.delivery !== 'NONE';
        if (!teachingUsed) {
          const fallback = await simplerVariant(item);
          if (fallback === null) {
            await resolveRound(false, 0, 'LADDER_EXHAUSTED');
            return;
          }
          item = fallback;
          onSimplified = true;
          await checkpoint();
          continue;
        }

        // Teaching ends in something the child can now answer (R1-2). This is the ladder's
        // simplified rung, whichever of the three routes supplied the question, so it scores the
        // post-teaching tier and the ladder terminates on it.
        item = await postTeachingAsk(item, delivery.definition);
        onSimplified = true;
        await checkpoint();
        continue;
      }

      if (step === 'SIMPLIFIED') {
        const simpler = await simplerVariant(item);
        if (simpler === null) {
          // Nothing easier exists. A grown-up who asked for it is told by the mission moving on,
          // not by being handed the same question back under an "easier" label.
          await resolveRound(
            false,
            0,
            simplifyRequestedByParent ? 'PARENT_OVERRIDE' : 'LADDER_EXHAUSTED',
          );
          return;
        }
        item = simpler;
        onSimplified = true;
        await checkpoint();
        continue;
      }

      await resolveRound(false, 0, 'LADDER_EXHAUSTED');
      return;
    }
  };

  /**
   * A both-required celebration a narrative stage carries (COOP-06, D-045 PS2-4).
   *
   * Resolved before the beat starts so the announcement and the story line are one moment rather
   * than two. Everything that can be wrong with it fails *soft*: a content id that resolves to
   * nothing, to an academic item, or to an action this build cannot observe leaves a diagnostic
   * and gives back an ordinary story beat. A celebration is the last thing that may strand a
   * child in front of a screen that will not move on.
   */
  interface SharedMoment {
    item: LearningContentItem;
    /** How many children the moment waits for. "Both" is everybody in the room — one, in a solo
     * sitting, which is the same moment with one child in it rather than a different one. */
    requiredPlayerCount: number;
  }

  const sharedMomentFor = async (stage: MissionStageDefinition): Promise<SharedMoment | null> => {
    const contentId = stage.sharedMomentContentId;
    if (contentId === undefined) return null;

    const unusable = (code: string): null => {
      analytics.recordContentDiagnostic({ contentId, code });
      return null;
    };

    const item = await itemById(contentId);
    if (item === null) return unusable('SHARED_MOMENT_MISSING');
    // Never an academic question, whatever a mission declares (module spec §11): a moment that
    // cannot be got wrong is the only kind of moment this path can run.
    if (item.kind !== 'SHARED_MOVEMENT') return unusable('SHARED_MOMENT_NOT_SHARED_MOVEMENT');
    if (!isSupportedResponseMode(item.expectedResponse)) {
      return unusable('SHARED_MOMENT_UNPLAYABLE');
    }

    return {
      item,
      requiredPlayerCount:
        item.expectedResponse.bothPlayersRequired === true
          ? Math.max(1, options.players.length)
          : 1,
    };
  };

  /**
   * The narrative beat (R1-12, GLP-10), and the both-required moment it may carry (COOP-06).
   *
   * A stage with nothing to answer used to be entered and left inside the same runtime turn, so
   * the intro was removed before a frame was drawn and the victory scene — where D-039(3) puts
   * the seal celebration — was replaced by the parent summary before anyone saw it. A mission
   * needs a beginning and an ending a child can actually see.
   *
   * Without a shared moment it is a pacing beat and nothing more: the wait is on the pause-aware
   * timer group, so it stops behind the overlay, and any action a child makes moves it along
   * rather than making them sit through it.
   *
   * With one, the beat becomes the moment. It is announced, the announced movement is what counts
   * (anything else a child does is a child moving, never a wrong answer), and it ends — always:
   *
   *   - both children move ⇒ it completes there and then;
   *   - one moves ⇒ the other has `bothRequiredMs.completionWindow`, then one gently encouraged
   *     `encouragementWindow`, and then it completes anyway with whoever joined in;
   *   - nobody moves ⇒ it completes at the end of the ordinary beat, with nobody.
   *
   * A child who cannot or will not raise their hands does not get to be the reason a mission
   * stopped. Nothing about any of it is recorded as evidence or ordered, and the one thing it can
   * earn — the shared cooperative action's team bonus — is withheld rather than deducted.
   */
  const narrativeBeat = async (stage: MissionStageDefinition): Promise<void> => {
    const moment = await sharedMomentFor(stage);
    if (isDisposed() || endSessionRequested) return;
    await runBeat(stage, moment);
  };

  const runBeat = (stage: MissionStageDefinition, moment: SharedMoment | null): Promise<void> =>
    new Promise<void>((resolve) => {
      let settled = false;
      let unsubscribe: (() => void) | null = null;
      /** The story beat's own duration. Cancelled once the moment is genuinely under way. */
      let beatTimer: RoundTimerId | null = null;
      /** Whichever of the moment's two windows is currently running. */
      let momentTimer: RoundTimerId | null = null;
      /** Who joined in. Put into roster order on the way out (R1-8), never movement order. */
      const participants: PlayerId[] = [];

      const matcher =
        moment === null
          ? null
          : createResponseMatcher({
              expected: moment.item.expectedResponse,
              tunables: options.tunables,
            });

      const teardown = (): void => {
        if (beatTimer !== null) {
          timers.cancel(beatTimer);
          beatTimer = null;
        }
        if (momentTimer !== null) {
          timers.cancel(momentTimer);
          momentTimer = null;
        }
        unsubscribe?.();
        abortRound = null;
      };

      /**
       * The beat is over because the sitting is: a teardown, or a grown-up's command.
       *
       * Nothing is said to have completed, because nothing did. A celebration that announced
       * itself as finished on the way out of an ended sitting would be a lie in a child's record.
       */
      const abandon = (): void => {
        if (settled) return;
        settled = true;
        teardown();
        resolve();
      };

      /** The moment is over, whoever joined in. It always ends this way. */
      const finish = (): void => {
        if (settled) return;
        settled = true;
        teardown();
        if (moment !== null) {
          /**
           * The shared cooperative action's team bonus (MVP notes §14, game design spec §17).
           *
           * Claimed only when the whole room joined in, and only when there is a room to join in
           * with: a solo child's pose is not cooperation, and bonusing it would move Phase 1's
           * accepted single-player totals (COOP-09).
           *
           * A moment that ended on the encouragement path simply does not claim it. That is a
           * withheld award and not a deduction (D-041(2)): the round's shards are untouched, the
           * score cannot move down, and nothing anywhere says a child cost the team anything.
           */
          if (
            moment.requiredPlayerCount >= 2 &&
            participants.length === moment.requiredPlayerCount
          ) {
            score.award({
              // Stage-keyed and therefore claimed once, even if a resumed sitting replays the
              // beat: the score controller is idempotent in this id.
              eventId: `${options.session.sessionId}.shared-moment-${stage.id}`,
              type: 'COOPERATIVE_ACTION',
              contentId: moment.item.id,
              stageId: stage.id,
              contributingPlayerIds: rosterOrder(participants),
              at: scheduler.now(),
            });
          }

          // Neutral, per child, and only for a child who actually moved. Never evidence.
          if (participants.length > 0) {
            analytics.recordBothRequiredMoment({ playerIds: rosterOrder(participants) });
          }
          options.onEvent({
            type: 'SHARED_MOMENT_COMPLETED',
            stageId: stage.id,
            contentId: moment.item.id,
            bothRequiredPlayerIds: rosterOrder(participants),
          });
        }
        resolve();
      };

      /** One nudge, addressed to the room. Never "waiting for you". */
      const encourage = (): void => {
        momentTimer = null;
        if (settled || moment === null) return;
        const hint = moment.item.hints[0];
        const text = hint?.spokenText ?? hint?.text;
        if (text !== undefined) {
          options.onEvent({
            type: 'SHARED_MOMENT_ENCOURAGED',
            stageId: stage.id,
            contentId: moment.item.id,
            text,
          });
          speak(text);
        }
        momentTimer = timers.after(options.tunables.bothRequiredMs.encouragementWindow, finish);
      };

      const joinIn = (playerId: PlayerId): void => {
        if (settled || moment === null) return;
        if (participants.includes(playerId)) return;
        participants.push(playerId);

        if (participants.length >= moment.requiredPlayerCount) {
          finish();
          return;
        }

        // Somebody is posing: this is no longer a story beat waiting to be skipped, it is a
        // moment waiting for the rest of the room.
        if (beatTimer !== null) {
          timers.cancel(beatTimer);
          beatTimer = null;
        }
        if (momentTimer !== null) return;
        momentTimer = timers.after(options.tunables.bothRequiredMs.completionWindow, encourage);
      };

      // So an override or teardown during the beat is not stuck behind it.
      abortRound = abandon;

      if (moment !== null) {
        options.onEvent({
          type: 'SHARED_MOMENT_PRESENTED',
          stageId: stage.id,
          contentId: moment.item.id,
          prompt: promptOf(moment.item),
          requiredPlayerCount: moment.requiredPlayerCount,
        });
        // So "say that again" and the after-pause re-read say the invitation rather than the
        // question before it.
        currentSpokenInstruction = moment.item.prompt.spokenInstruction;
      }

      /** The invitation, said after the story line rather than over the top of it. */
      const announce = (): void => {
        if (settled || moment === null || !isRunning()) return;
        speak(moment.item.prompt.spokenInstruction);
      };

      // The line the stage itself carries, said before anything can interrupt it.
      if (stage.spokenIntroduction === undefined) {
        announce();
      } else if (moment === null) {
        speak(stage.spokenIntroduction);
      } else {
        // Chained on the narrator rather than on a guessed duration: `speak` interrupts by
        // design, so announcing straight away would cut the story line off mid-sentence. A
        // narrator that never finishes leaves the invitation drawn but unspoken — the moment
        // still runs on its own timers, which is the failure that costs the least.
        void options.narration
          .speak(stage.spokenIntroduction, { interrupt: true })
          .catch(() => {})
          .then(announce);
      }

      // Onsets only (R1-12). A retraction — hands coming down, standing back up — is not a child
      // moving the story along; it is very often the *end* of the answer that opened this stage,
      // or a browser blur. Either way it must not delete a beat before it has been seen.
      unsubscribe = options.input.subscribe((event: ThriveInputEvent) => {
        if (!isRunning()) return;
        if (!isOnsetInputEvent(event)) return;
        if (matcher === null) {
          finish();
          return;
        }
        const verdict = matcher.observe(event);
        if (verdict.kind === 'IGNORED') return;
        // The announced movement is what joins in. Anything else is a child moving about during
        // a celebration, which is not a wrong answer and is not recorded as anything at all.
        if (!verdict.correct) return;
        joinIn(verdict.playerId);
      });

      beatTimer = timers.after(options.tunables.timersMs.narrativeBeat, finish);
    });

  /**
   * The band this position is served at, asked of the module's own policy (R1-13, SKL-04).
   *
   * The policy was a provider nobody called, so every item came from the statically configured
   * band and TEA-02's "first time this concept appears" trigger could not fire. Bounded
   * deliberately: it runs once per content position, never per answer; it can only choose a
   * *safer* band, never a harder one; and nothing about it is said to the child (architecture §12,
   * "no level down"). A player the platform has stored no estimates for — every guest, by
   * construction (D-025) — is left on the band a grown-up configured, because treating "no
   * history" as "every concept is new" would silently drop them a band for the whole mission.
   *
   * "New concept" is decided per player, over the players who have history at all: see the rule
   * beside `playersWithHistory` below (SKL-05 ruling, Slice E).
   */
  const adaptItem = async (
    stage: MissionStageDefinition,
    item: LearningContentItem,
  ): Promise<LearningContentItem> => {
    const policy = options.adaptation;
    // The content POSITION, not the slot the item is a variant of (R1-13). A replay equivalent
    // carries no `slotId` by design, and keying off one skipped adaptation at every position of
    // every "play again" sitting — the safer-band rule silently did not exist on that path.
    const positionId = item.slotId ?? item.positionId;
    if (policy === undefined || positionId === undefined) return item;

    const grouped = await storedEstimatesByPlayer();
    const estimates = grouped.flat();
    if (estimates.length === 0) return item;

    const first = options.players[0];
    adaptationState ??= policy.getStartingState(
      // A `GuestPlayer` is the shape the policy asks for and the shape a runtime player already
      // is: an id, a lane and a band. A named child's profile is not loaded here on purpose —
      // the policy reads history from the estimates, not from the profile record.
      {
        playerId: first?.playerId ?? '',
        lane: first?.lane ?? 'left',
        band: teamBandOf(options.players),
      },
      options.mission,
    );

    /**
     * Is this position a first meeting, for anybody in the room? (SKL-05 ruling, Slice E.)
     *
     * A concept is new if any player the platform *has* history for has not met it. Only players
     * with stored estimates are asked: a guest, or a child on their first ever sitting, holds no
     * history by construction (D-025), and counting their silence as "has not met this" would
     * drop the team a band at every position of the whole mission — the harm D-042(8) exists to
     * prevent. So a named child beside a guest behaves exactly as a named child alone, and two
     * named children where one has never seen the skill meet it at the gentler band.
     *
     * Team-union newness — "somebody here knows it, so it is review" — got that last case wrong,
     * and it is the case the second child's profile exists for (D-045 PS2-7).
     */
    const playersWithHistory = grouped.filter((entries) => entries.length > 0);
    const isNewConcept = playersWithHistory.some(
      (entries) =>
        !item.skillIds.some((skillId) => entries.some((entry) => entry.skillId === skillId)),
    );

    const selection = policy.selectDifficulty(adaptationState, estimates, {
      playerMode: options.playerMode,
      players: options.players,
      stage,
      isNewConcept,
    });

    // Never upward. A policy that returns a harder band is ignored rather than obeyed.
    if (BAND_ORDER.indexOf(selection.band) >= BAND_ORDER.indexOf(item.difficultyBand)) return item;

    const results = await options.content.getContent(
      { slotIds: [positionId], band: selection.band },
      selectionContext(),
    );
    return results.find((candidate) => candidate.difficultyBand === selection.band) ?? item;
  };

  const runStage = async (stage: MissionStageDefinition): Promise<void> => {
    options.onEvent({
      type: 'STAGE_ENTERED',
      stageId: stage.id,
      stageType: stage.type,
      label: stage.label ?? stage.id,
    });

    if (!isScoredStage(stage)) {
      if (isDisposed() || endSessionRequested) return;
      await narrativeBeat(stage);
      return;
    }

    const selected = await options.content.getContent(
      stage.contentSelector ?? {},
      selectionContext(),
    );

    const cap =
      stage.completionRule.type === 'ROUND_COUNT' ? stage.completionRule.rounds : selected.length;

    // A resumed mission must not replay what it already finished, and a provider may legitimately
    // return fewer items than the stage asks for (D-035) — so this iterates what came back.
    const remaining = selected
      .filter((item) => !completedContentIds.includes(item.id))
      .slice(0, cap);

    for (const item of remaining) {
      if (isDisposed() || endSessionRequested) return;
      await runRound(stage, item);
    }
  };

  /**
   * Closes the sitting out.
   *
   * Two ways in: the mission reached its end, or a grown-up ended the sitting from the pause
   * overlay (`END_SESSION`, game design spec §32). The second is not a completion, and saying so
   * once here is what keeps every downstream surface honest — the child's end state, the parent
   * summary's "finished" line, the completion reward, and the stored progress.
   */
  /**
   * The once-per-session progression transaction (R1-11, D-039(5)).
   *
   * Load or create this player's progress, let the game fold the finished session into it, save.
   * The channel was designed and then never called from anywhere but a unit test, so a child who
   * earned *Number Master* and came back the next evening found no completed mission, no shards
   * and no badge — and the module's no-downgrade rule had never once run in production.
   *
   * Per player, because progress is per player. A failure is reported through the caller's error
   * path but never takes the finished mission away: a full disk is not something a five-year-old
   * did wrong.
   */
  const persistProgression = async (session: CompletedGameSession): Promise<void> => {
    const progression = options.progression;
    const store = options.repositories.progress;
    if (progression === undefined || store === undefined) return;

    const sessionId = options.session.sessionId;
    const teamScore = session.summary.teamScore;

    for (const player of options.players) {
      // A guest keeps no progress by construction (D-025), and must not be handed the progress
      // the last named child left in their lane either (R1-3).
      const subjectId = learningSubjectIdOf(player);
      if (subjectId === null) continue;

      try {
        const existing = await store.getGameProgress(subjectId, options.gameId);
        const base = existing ?? progression.createInitialProgress(subjectId);

        /**
         * Superseding, not accumulating (R2-1).
         *
         * "End the sitting safely" then "Continue playing" is one session that completes twice,
         * on purpose: the snapshot is kept and the session id is reused so the rounds already
         * scored stay scored (D-041(2)/(3)). What the module is handed is therefore what this
         * session has already contributed, so it can fold in the difference and count the
         * mission once, rather than adding an evening's whole running total a second time.
         */
        const history = base.appliedSessions ?? [];
        const previouslyApplied = history.find((entry) => entry.sessionId === sessionId);

        const applied = progression.applySession(
          base,
          session,
          previouslyApplied === undefined ? {} : { previouslyApplied },
        );

        await store.saveGameProgress(subjectId, options.gameId, {
          ...applied,
          appliedSessions: [
            ...history.filter((entry) => entry.sessionId !== sessionId),
            { sessionId, teamScore },
          ].slice(-APPLIED_SESSION_HISTORY_LIMIT),
        });
      } catch {
        // Storage that will not take the write is a storage problem. The mission still finished,
        // the child still saw their badge, and the next sitting will try again.
      }
    }
  };

  const complete = async (endedEarly: boolean): Promise<void> => {
    const completedAt = clock();

    // Belt for the exits that never reach a round's closure — a sitting ended between rounds, or
    // while a teaching moment held one — where a settlement could still be owed a checkpoint
    // (R2-1). An early end keeps its snapshot, so a late checkpoint there is the snapshot the
    // family comes back to.
    await settleOutstandingMisses();

    // The mission-completion bonus belongs to a mission that completed. Withholding it is not a
    // deduction (the score never moves down); it is simply not claiming something that did not
    // happen.
    if (!endedEarly) {
      score.award({
        eventId: `${options.session.sessionId}.mission-completed`,
        type: 'MISSION_COMPLETED',
        contributingPlayerIds: options.players.map((player) => player.playerId),
        at: scheduler.now(),
      });
    }

    // Computed once and carried on the session as well as the event. The child sees this reward
    // and a `GameProgressionProvider` stores this reward; computing it twice would let the two
    // disagree (D-038(6)).
    const reward = score.getCompletionReward({ endedEarly });

    const session = assembleCompletedSession({
      record: sessionRecord(),
      score: score.getState(),
      analytics,
      startedAtMs,
      completedAtMs: scheduler.now(),
      completedAt: completedAt.toISOString(),
      reward,
      ...(endedEarly ? { endedEarly: true } : {}),
    });

    // Evidence was written round by round, so completion only closes the session out.
    await options.repositories.sessions.completeSession(session.record.sessionId, session.summary);
    await persistProgression(session);
    // The mission is finished, so there is nothing to carry on from. A sitting a grown-up ended
    // early keeps its snapshot: the summary tells them it is "ready to carry on next time", and
    // clearing it here would make that a lie and throw away the rounds the child did play.
    // Through the same chain as every save (R1-1): a checkpoint still in flight when the mission
    // ends would otherwise land after the clear and resurrect a mission that is over.
    if (!endedEarly) {
      // A clear that cannot be verified is reported by the repository now (S1-5); it stays in the
      // erasure backlog and is re-attempted, and it must not stop a child seeing their badge.
      try {
        await enqueueSnapshotWrite(() => options.repositories.missionProgress.clearProgress());
      } catch {
        // Retried by the store's own backlog and by the next launch's pass.
      }
      // The identity goes with the snapshot it describes (D-054(2)). Through the same chain, so a
      // checkpoint still in flight cannot land between the two and leave a sitting half-recorded.
      const identity = options.repositories.launchIdentity;
      if (identity !== undefined) {
        try {
          await enqueueSnapshotWrite(() => identity.clear());
        } catch {
          // Same rule: the mission finished, and the stale identity is refused on the next launch.
        }
      }
    }

    status = 'completed';
    options.onEvent({ type: 'MISSION_COMPLETED', session, reward, endedEarly });
  };

  return {
    get status(): MissionRuntimeStatus {
      return status;
    },

    sceneServices: { scoring: score, teaching, analytics },

    async start(resumeFrom?: MissionProgressSnapshot | null): Promise<void> {
      if (status !== 'idle') return;
      status = 'running';
      startedAtMs = scheduler.now();

      if (resumeFrom != null) {
        completedContentIds.push(...resumeFrom.completedContentIds);
        roundIndex = resumeFrom.roundIndex;
        // Sequence provenance travels with the snapshot, not with the session-only launch config
        // a refresh recomputes from defaults — otherwise a refreshed replay would resume into the
        // curated sequence and serve every position again (R1-8).
        useCuratedSequence = resumeFrom.useCuratedSequence ?? useCuratedSequence;
        // The round that was interrupted carries on from where it was, rather than restarting as
        // a fresh first attempt on the position's own question (R1-3).
        resumeInto = {
          attemptNumber: resumeFrom.attemptNumber,
          onSimplified: resumeFrom.onSimplified,
          sawIncorrectAnswer: resumeFrom.sawIncorrectAnswer,
          observedPlayerIds: resumeFrom.observedPlayerIds,
          ...(resumeFrom.contentId === undefined ? {} : { contentId: resumeFrom.contentId }),
        };
        teaching.restore(resumeFrom.teachingConsumedPlayerIds);
        analytics.restore(resumeFrom.progress);
        analytics.restoreSkillPractice(resumeFrom.skillPractice ?? {});
        const restored: ScoreState = {
          teamScore: resumeFrom.score.teamScore,
          roundsScored: resumeFrom.score.roundsScored,
          participation: resumeFrom.score.participation,
          appliedEventIds: resumeFrom.score.appliedEventIds,
          ...(resumeFrom.score.detail === undefined ? {} : { detail: resumeFrom.score.detail }),
        };
        score.restore(restored);
      }

      options.onEvent({
        type: 'MISSION_STARTED',
        missionId: options.mission.id,
        sessionId: options.session.sessionId,
        totalRounds: options.mission.stages.reduce(
          (total, stage) =>
            total + (stage.completionRule.type === 'ROUND_COUNT' ? stage.completionRule.rounds : 0),
          0,
        ),
      });

      await options.repositories.sessions.startSession(sessionRecord());

      for (const stage of options.mission.stages) {
        if (isDisposed() || endSessionRequested) break;
        await runStage(stage);
      }

      if (isDisposed()) return;
      await complete(endSessionRequested);
    },

    pause(): void {
      if (status !== 'running') return;
      status = 'paused';
      // Before the clocks stop, so nothing armed is left holding a timer that resumes with it
      // (D-059 PS3-2, S1-4). A parent's Pause, a camera that lost the child and a mid-mission
      // `RESTART_CALIBRATION` all arrive here, which is why the rule lives here and not beside any
      // one of them.
      discardArmedAnswers?.();
      timers.pause();
      // A teaching moment's own duration cap is suspended with the round ladder, not left
      // running behind the pause overlay (D-036(9)).
      teaching.pause();
      options.narration.stop();
      options.onEvent({ type: 'MISSION_PAUSED' });
    },

    resume(): void {
      if (status !== 'paused') return;
      status = 'running';
      timers.resume();
      teaching.resume();
      options.onEvent({ type: 'MISSION_RESUMED' });
      // Coming back to a prompt you can no longer remember is its own kind of failure.
      if (currentSpokenInstruction !== null) speak(currentSpokenInstruction);
    },

    repeatInstruction(): void {
      if (currentSpokenInstruction === null) return;
      speak(currentSpokenInstruction);
    },

    /**
     * A grown-up's mid-mission control (game design spec §32, D-041).
     *
     * Every one of these is pressed from the pause overlay, and a paused mission is very often
     * *not* waiting for an answer — a teaching moment holds the round back, and its own timer is
     * suspended by the same pause. So a command with no round to interrupt is queued rather than
     * dropped, and ending the sitting cancels the moment outright instead of waiting for a Resume
     * the grown-up has no reason to press (R1-5).
     */
    applyParentOverride(command: ParentOverrideCommand): void {
      if (command.type === 'END_SESSION') {
        endSessionRequested = true;
        pendingOverride = null;
        // Whatever is running stops now: an ended sitting must not be held open by a teaching
        // moment whose clock the pause has already stopped.
        teaching.cancel();
        abortRound?.({ kind: 'OVERRIDE', command });
        return;
      }
      if (command.type === 'SKIP_ROUND' || command.type === 'SIMPLIFY') {
        if (abortRound !== null) {
          abortRound({ kind: 'OVERRIDE', command });
          return;
        }
        pendingOverride = command;
      }
      // RESTART_CALIBRATION belongs to the camera phases; it is not a round-level action.
    },

    dispose(): void {
      if (isDisposed()) return;
      status = 'disposed';
      timers.cancelAll();
      // A teaching moment waits on its own timer group, so a stage torn down mid-moment would
      // otherwise leave the mission's promise chain waiting for a clock nobody will advance.
      teaching.cancel();
      abortRound?.({ kind: 'ABORTED' });
      options.narration.stop();
    },
  };
}
